import React, { useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  X,
  Upload,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Loader2,
  FileSpreadsheet,
  Sparkles,
} from 'lucide-react';
import { RecurringActionLine } from '../../types/database';
import { formatDateSafe } from '../../lib/dateUtils';
import type { RecurringPrefillData } from './AddRecurringActionModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RawRow {
  /** date string in any accepted format */
  date: string;
  description: string;
  amount: number;
}

export interface DiscoverySuggestion {
  /** Canonical description (trimmed, original casing) */
  description: string;
  /** Exact amount value (all occurrences share the same amount) */
  amount: number;
  /** Number of occurrences found in the file */
  occurrences: number;
  /** All source dates from the file (YYYY-MM-DD) */
  sourceDates: string[];
  /** Best-guess start date for the wizard pre-fill (latest date in the group) */
  suggestedStartDate: string;
  /** Guessed frequency label (Hebrew) */
  guessedFrequency: string;
}

export interface RecurringDiscoveryWizardProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing recurring actions so we can filter duplicates */
  existingRecurringActions: RecurringActionLine[];
  /** Called when user clicks "אשר" – opens the Add Recurring Action wizard */
  onApprove: (prefill: RecurringPrefillData) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Sanitise a raw date cell (Excel serial, ISO, DD/MM/YYYY) → "YYYY-MM-DD".
 * Returns empty string when the value cannot be parsed.
 */
function parseCellDate(raw: unknown): string {
  if (typeof raw === 'number') {
    // Excel serial date
    if (raw >= 1 && raw <= 2958465) {
      const parsed = XLSX.SSF.parse_date_code(raw);
      if (parsed && parsed.y && parsed.m && parsed.d) {
        const mm = String(parsed.m).padStart(2, '0');
        const dd = String(parsed.d).padStart(2, '0');
        return `${parsed.y}-${mm}-${dd}`;
      }
    }
    return '';
  }

  const s = String(raw ?? '').trim();
  if (!s) return '';

  // YYYY-MM-DD (with optional time suffix)
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  // DD/MM/YYYY
  const dmyMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmyMatch) {
    const mm = (dmyMatch[2] ?? '').padStart(2, '0');
    const dd = (dmyMatch[1] ?? '').padStart(2, '0');
    const yyyy = dmyMatch[3] ?? '';
    return `${yyyy}-${mm}-${dd}`;
  }

  return '';
}

/** Parse an amount cell → number (or NaN) */
function parseCellAmount(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  const s = String(raw ?? '').replace(/,/g, '').replace(/[₪$€£]/g, '').trim();
  return parseFloat(s);
}

/**
 * Guess a human-readable Hebrew frequency label from the sorted date list.
 * Calculates median gap between consecutive dates.
 */
function guessFrequencyLabel(sortedDates: string[]): string {
  if (sortedDates.length < 2) return 'לא ידוע';

  const ms: number[] = [];
  for (let i = 1; i < sortedDates.length; i++) {
    const dateA = sortedDates[i - 1] ?? '';
    const dateB = sortedDates[i] ?? '';
    if (!dateA || !dateB) continue;
    const a = new Date(dateA).getTime();
    const b = new Date(dateB).getTime();
    if (!isNaN(a) && !isNaN(b)) ms.push(b - a);
  }
  if (ms.length === 0) return 'לא ידוע';

  // Median
  ms.sort((a, b) => a - b);
  const medianMs = ms[Math.floor(ms.length / 2)] ?? 0;
  const medianDays = medianMs / (1000 * 60 * 60 * 24);

  if (medianDays <= 1.5) return 'יומי';
  if (medianDays <= 9) return 'שבועי';
  if (medianDays <= 18) return 'דו-שבועי';
  if (medianDays <= 40) return 'חודשי';
  if (medianDays <= 75) return 'דו-חודשי';
  if (medianDays <= 100) return 'רבעוני';
  if (medianDays <= 200) return 'חצי שנתי';
  return 'שנתי';
}

/**
 * Parse CSV/XLSX buffer and return an array of {date, description, amount}.
 * Expects headers: תאריך | תיאור | סכום (trimmed, in the first 3 columns).
 * Throws if the file cannot be parsed or headers are wrong.
 */
function parseFile(file: File): Promise<RawRow[]> {
  return new Promise((resolve, reject) => {
    const ext = (file.name.split('.').pop() ?? '').toLowerCase();

    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
        complete(results) {
          try {
            const rows: RawRow[] = [];
            for (const row of results.data as Record<string, unknown>[]) {
              const date = parseCellDate(row['תאריך']);
              const description = String(row['תיאור'] ?? '').trim();
              const amount = parseCellAmount(row['סכום']);
              if (date && description && !isNaN(amount)) {
                rows.push({ date, description, amount });
              }
            }
            resolve(rows);
          } catch (e) {
            reject(e);
          }
        },
        error(err) {
          reject(new Error(err.message));
        },
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target!.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          const sheetName = wb.SheetNames[0] ?? '';
          const ws = wb.Sheets[sheetName];
          if (!ws) return resolve([]);
          // raw:true so dates come as serial numbers
          const json: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true });

          if (json.length < 2) return resolve([]);

          // Find header row – first row whose first 3 cells contain the expected headers
          const rawHeader: string[] = (json[0] as unknown[]).map((c) => String(c ?? '').trim());
          const h0 = rawHeader[0] ?? '';
          const h1 = rawHeader[1] ?? '';
          const h2 = rawHeader[2] ?? '';
          if (h0 !== 'תאריך' || h1 !== 'תיאור' || h2 !== 'סכום') {
            return reject(new Error('כותרות הקובץ אינן תואמות. דרוש: תאריך | תיאור | סכום'));
          }

          const rows: RawRow[] = [];
          for (let i = 1; i < json.length; i++) {
            const rowArr = json[i] as unknown[];
            const date = parseCellDate(rowArr[0]);
            const description = String(rowArr[1] ?? '').trim();
            const amount = parseCellAmount(rowArr[2]);
            if (date && description && !isNaN(amount)) {
              rows.push({ date, description, amount });
            }
          }
          resolve(rows);
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = () => reject(new Error('שגיאה בקריאת הקובץ'));
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error('סוג קובץ לא נתמך. יש להעלות CSV או XLSX.'));
    }
  });
}

/**
 * Group rows by (description, amount), keep groups with ≥2 occurrences,
 * filter out groups already present in existingRecurringActions,
 * and return sorted suggestions (most occurrences first).
 */
function buildSuggestions(
  rows: RawRow[],
  existingRecurringActions: RecurringActionLine[],
): DiscoverySuggestion[] {
  // Build a set of existing keys: "description|||amount"
  const existingKeys = new Set<string>(
    existingRecurringActions.map((a) => `${a.description.trim().toLowerCase()}|||${a.amount}`),
  );

  // Group
  const map = new Map<string, { description: string; amount: number; dates: string[] }>();
  for (const row of rows) {
    const key = `${row.description.trim().toLowerCase()}|||${row.amount}`;
    if (!map.has(key)) {
      map.set(key, { description: row.description.trim(), amount: row.amount, dates: [] });
    }
    map.get(key)!.dates.push(row.date);
  }

  const suggestions: DiscoverySuggestion[] = [];
  for (const [key, value] of map.entries()) {
    if (value.dates.length < 2) continue; // skip singletons
    if (existingKeys.has(key)) continue;   // already a recurring action

    const sortedDates = [...value.dates].sort();
    suggestions.push({
      description: value.description,
      amount: value.amount,
      occurrences: value.dates.length,
      sourceDates: sortedDates,
      suggestedStartDate: sortedDates[sortedDates.length - 1] ?? sortedDates[0] ?? '',
      guessedFrequency: guessFrequencyLabel(sortedDates),
    });
  }

  // Sort by occurrences desc, then description asc
  suggestions.sort((a, b) => b.occurrences - a.occurrences || a.description.localeCompare(b.description, 'he'));
  return suggestions;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface SuggestionCardProps {
  suggestion: DiscoverySuggestion;
  onApprove: () => void;
  onDismiss: () => void;
}

function SuggestionCard({ suggestion, onApprove, onDismiss }: SuggestionCardProps) {
  const [expanded, setExpanded] = useState(false);

  const formattedAmount = new Intl.NumberFormat('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(suggestion.amount));
  const sign = suggestion.amount < 0 ? '-' : '';

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm truncate" title={suggestion.description}>
            {suggestion.description}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
            <span className="font-medium text-gray-800">
              {sign}₪{formattedAmount}
            </span>
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              {suggestion.guessedFrequency}
            </span>
            <span>{suggestion.occurrences} הופעות</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onApprove}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            אשר
          </button>
          <button
            onClick={onDismiss}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            <XCircle className="h-3.5 w-3.5" />
            דחה
          </button>
        </div>
      </div>

      {/* Expand/collapse source dates */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="mt-2 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 transition-colors"
      >
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        הצג פעולות מקור ({suggestion.sourceDates.length})
      </button>

      {expanded && (
        <ul className="mt-2 space-y-1 pr-1 border-r-2 border-primary-100 pl-2 text-xs text-gray-600">
          {suggestion.sourceDates.map((d, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="text-gray-400">•</span>
              <span>{formatDateSafe(d)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function RecurringDiscoveryWizard({
  isOpen,
  onClose,
  existingRecurringActions,
  onApprove,
}: RecurringDiscoveryWizardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<DiscoverySuggestion[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      setParsing(true);
      setParseError(null);
      setFileName(file.name);
      try {
        const rows = await parseFile(file);
        const newSuggestions = buildSuggestions(rows, existingRecurringActions);
        // Replace existing suggestions completely
        setSuggestions(newSuggestions);
        if (newSuggestions.length === 0) {
          setParseError('לא נמצאו פעולות חוזרות חדשות בקובץ זה.');
        }
      } catch (e) {
        setParseError(e instanceof Error ? e.message : 'שגיאה לא ידועה בעיבוד הקובץ.');
        setSuggestions([]);
      } finally {
        setParsing(false);
      }
    },
    [existingRecurringActions],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDismiss = (index: number) => {
    setSuggestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleApprove = (suggestion: DiscoverySuggestion, index: number) => {
    setSuggestions((prev) => prev.filter((_, i) => i !== index));
    onApprove({
      description: suggestion.description,
      amount: suggestion.amount,
      start_date: suggestion.suggestedStartDate,
    });
  };

  const handleClose = () => {
    setParsing(false);
    setParseError(null);
    setSuggestions([]);
    setFileName(null);
    setIsDragging(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">גלה פעולות קבועות מקובץ</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Upload area */}
        <div className="px-6 pt-4 flex-shrink-0">
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragging
                ? 'border-primary-400 bg-primary-50'
                : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            {parsing ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                <p className="text-sm text-gray-600">מנתח קובץ…</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet className="h-8 w-8 text-gray-400" />
                <p className="text-sm font-medium text-gray-700">
                  {fileName ? `${fileName} — לחץ/גרור להחלפה` : 'לחץ או גרור קובץ CSV / XLSX לכאן'}
                </p>
                <p className="text-xs text-gray-400">כותרות נדרשות: תאריך | תיאור | סכום</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={handleFileChange}
          />

          {parseError && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
              <Upload className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>{parseError}</p>
            </div>
          )}
        </div>

        {/* Suggestions list */}
        {suggestions.length > 0 && (
          <>
            <div className="px-6 pt-3 pb-2 flex-shrink-0">
              <p className="text-sm text-gray-600">
                נמצאו <span className="font-semibold text-gray-900">{suggestions.length}</span> פעולות קבועות חדשות —
                אשר כל אחת כדי להוסיף לאשף הפעולות הקבועות.
              </p>
            </div>
            <div className="px-6 pb-6 flex-1 overflow-y-auto space-y-3 min-h-0">
              {suggestions.map((s, i) => (
                <SuggestionCard
                  key={`${s.description}|||${s.amount}|||${i}`}
                  suggestion={s}
                  onApprove={() => handleApprove(s, i)}
                  onDismiss={() => handleDismiss(i)}
                />
              ))}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  );
}
